import React, { useEffect, useRef } from "react";
import { MOTION } from "../styles/motionTokens";
import { Z } from "../styles/zLayers";
import { useDismissible } from "../native/dismissibleStack";

/**
 * One-time "first card in collection" moment.
 * Primary CTA should push toward trading (wired by parent via onFindTraders).
 *
 * LAUNCH FIX (P0 area B, docs/LAUNCH_REMEDIATION_2026-09-26.md): when the first
 * card landed in the VAULT (the market "+" and bulk-add paths), nobody can
 * match with it — matching only uses Trade Binder / For Sale cards with an
 * owner photo. The old primary CTA sent that user straight to the map, where
 * their card could never appear. When `onListForTrade` is supplied (the Vault
 * case), the primary action is "List it for trade" instead, with one honest
 * sentence about the Vault, and the moment does NOT auto-dismiss — it carries
 * a decision now, not just a flourish.
 */
const FirstCardCelebration = ({ open, onClose, onFindTraders, onListForTrade, imageUrl, cardName }) => {
  const dialogRef = useRef(null);

  // This surface tells a screen reader it is modal (`aria-modal` below), and it
  // was the last of the app's SIX such overlays not to claim Back. Unregistered,
  // it did not swallow Back at all: `decideBackAction` fell straight past its
  // `overlayOpen` arm to `HISTORY_BACK`, so the page navigated out from under a
  // celebration that stayed on screen — `CollectionProvider` hosts this above the
  // Router, so the overlay outlives the navigation. (App exit needs a home route
  // AND a second press inside the confirm window; this moment fires on /collection,
  // /pokemontcg and /wishlist, none of which are home routes.) On the web Escape
  // did nothing at all, because `escapeToDismiss` only ever sees what is
  // registered here. `onClose` is deliberately the same callback the × and the
  // hold timer already run, which is the property backButton.overlays.test.jsx
  // asserts for every other overlay.
  useDismissible(open, onClose, "first-card-celebration");

  useEffect(() => {
    if (!open || onListForTrade) return undefined;
    const t = setTimeout(() => onClose?.(), MOTION.firstCardHoldMs);
    return () => clearTimeout(t);
  }, [open, onClose, onListForTrade]);

  /**
   * Focus management, because this really is an `aria-modal` dialog.
   *
   * Same defect and same remedy as the sibling this pattern was settled on,
   * `TradeCompletionMoment` (effect at :75-80, `tabIndex={-1}` at :95):
   * `aria-modal="true"` asks assistive tech to hide everything outside, and
   * then focus never moved in, leaving the user parked on a node this surface
   * had just declared invisible.
   *
   * THE ROOT TAKES FOCUS, NOT THE "Find traders nearby" CTA. That CTA navigates away,
   * and arriving pre-armed under Enter would fire a route change on a stray
   * press during a celebration the user did not ask for. It is also the reason
   * this is focus-IN only: there is no opener control to restore to, because
   * the add that triggered this is what unmounts the surface behind it.
   *
   * THIS IS NOT A FOCUS TRAP AND MUST NOT BE READ AS ONE.
   */
  useEffect(() => {
    // UNPINNABLE BY CONSTRUCTION, and kept anyway — `[#103-MORE-4]`. The
    // component early-returns `null` when closed (just below), so the effect
    // body could not find a ref to focus even with this line deleted: there is
    // no input that makes its absence observable, and a mutant that removes it
    // survives the whole suite. It is defensive symmetry with the render guard,
    // NOT a tested arm — do not read the suite's green as covering it. The
    // `does not touch focus while closed` test passes with or without it.
    if (!open) return;
    // The root is a <div>, so this only does anything because of the
    // `tabIndex={-1}` below; without it focus() is a silent no-op.
    dialogRef.current?.focus();
  }, [open]);

  if (!open) return null;

  const goTraders = () => {
    onClose?.();
    onFindTraders?.();
  };
  const goList = () => {
    onClose?.();
    onListForTrade?.();
  };

  return (
    <div
      ref={dialogRef}
      data-testid="first-card-celebration"
      data-motion="binder-slot"
      role="dialog"
      aria-modal="true"
      aria-label="First card in your collection"
      // A <div> is not focusable without this, so the effect above would call
      // focus() to no effect and leave the user exactly where they were. -1 so
      // the root can hold focus without becoming a Tab stop of its own.
      tabIndex={-1}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: Z.celebration,
        background: "rgba(15,23,42,0.78)",
        backdropFilter: "blur(8px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        fontFamily: "Inter, sans-serif",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 340,
          background: "#0f172a",
          borderRadius: 20,
          padding: "28px 22px 22px",
          textAlign: "center",
          border: "1px solid rgba(139,92,246,0.35)",
          boxShadow: "0 24px 60px rgba(0,0,0,0.5)",
          animation: `firstCardIn ${MOTION.binderSnapMs}ms cubic-bezier(0, 0, 0.2, 1)`,
          position: "relative",
        }}
      >
        <button
          type="button"
          aria-label="Close"
          data-testid="first-card-close"
          onClick={onClose}
          style={{
            position: "absolute",
            top: 12,
            right: 12,
            background: "transparent",
            border: "none",
            color: "#94a3b8",
            fontSize: 20,
            cursor: "pointer",
            lineHeight: 1,
          }}
        >
          ×
        </button>

        <div
          data-testid="first-card-sleeve"
          style={{
            margin: "0 auto 18px",
            width: 140,
            height: 196,
            borderRadius: 12,
            padding: 8,
            background: "linear-gradient(145deg, rgba(255,255,255,0.12), rgba(255,255,255,0.04))",
            border: "1px solid rgba(255,255,255,0.18)",
            boxShadow: "inset 0 0 0 1px rgba(196,181,253,0.25)",
            animation: `firstCardSlot ${MOTION.binderSnapMs}ms ease`,
          }}
        >
          <img
            src={imageUrl || "https://placehold.co/120x168?text=Card"}
            alt={cardName || "Your first card"}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              borderRadius: 8,
              boxShadow: "0 0 24px rgba(167,139,250,0.45)",
              animation: `firstCardFoil ${MOTION.foilFlashMs}ms ease ${MOTION.binderSnapMs * 0.4}ms both`,
            }}
          />
        </div>

        <div
          style={{
            fontFamily: "Georgia, 'Times New Roman', serif",
            fontSize: 22,
            fontWeight: 700,
            color: "#e2e8f0",
            letterSpacing: "-0.02em",
            marginBottom: 6,
          }}
        >
          First card in your collection
        </div>
        <div style={{ color: "#94a3b8", fontSize: 14, marginBottom: onListForTrade ? 10 : 20 }}>
          Nice pull. Your vault starts here.
        </div>
        {onListForTrade && (
          <div
            data-testid="first-card-vault-explainer"
            style={{ color: "#cbd5e1", fontSize: 13, lineHeight: 1.45, marginBottom: 18 }}
          >
            Vault cards show on your profile but can&apos;t be traded. Want to trade this one? Put it in your
            Trade Binder with a photo — that&apos;s what lets nearby collectors match with you.
          </div>
        )}

        {onListForTrade && (
          <button
            type="button"
            data-testid="first-card-list-for-trade"
            onClick={goList}
            style={{
              width: "100%",
              padding: "13px 16px",
              borderRadius: 10,
              border: "none",
              background: "#6d28d9",
              color: "#fff",
              fontWeight: 700,
              fontSize: 14,
              cursor: "pointer",
              fontFamily: "Inter, sans-serif",
              marginBottom: 10,
            }}
          >
            List it for trade
          </button>
        )}
        {!onListForTrade && (
          <button
            type="button"
            data-testid="first-card-find-traders"
            onClick={goTraders}
            style={{
              width: "100%",
              padding: "13px 16px",
              borderRadius: 10,
              border: "none",
              background: "#6d28d9",
              color: "#fff",
              fontWeight: 700,
              fontSize: 14,
              cursor: "pointer",
              fontFamily: "Inter, sans-serif",
              marginBottom: 10,
            }}
          >
            Find traders nearby
          </button>
        )}
        <button
          type="button"
          data-testid="first-card-add-another"
          onClick={onClose}
          style={{
            width: "100%",
            padding: "12px 16px",
            borderRadius: 10,
            border: "1.5px solid rgba(255,255,255,0.2)",
            background: "transparent",
            color: "#e2e8f0",
            fontWeight: 600,
            fontSize: 14,
            cursor: "pointer",
            fontFamily: "Inter, sans-serif",
          }}
        >
          {onListForTrade ? "Keep it in my Vault" : "Add another"}
        </button>
      </div>

      <style>{`
        @keyframes firstCardIn {
          from { transform: translateY(20px) scale(0.96); opacity: 0; }
          to { transform: translateY(0) scale(1); opacity: 1; }
        }
        @keyframes firstCardSlot {
          from { transform: translateY(-12px) rotate(-4deg); opacity: 0.5; }
          to { transform: translateY(0) rotate(0deg); opacity: 1; }
        }
        @keyframes firstCardFoil {
          0% { filter: brightness(1); }
          40% { filter: brightness(1.25) saturate(1.2); }
          100% { filter: brightness(1); }
        }
      `}</style>
    </div>
  );
};

export default FirstCardCelebration;
